export interface LocalNotification {
  id: string;
  title: string;
  body: string;
  createdAt: string;
  read: boolean;
  targetUserId?: string;
  targetRole?: string;
  actionUrl?: string;
  eventType?: string;
}

const STORAGE_KEY = "hospitol.notifications.inbox";

function readAll(): LocalNotification[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as LocalNotification[]) : [];
  } catch {
    return [];
  }
}

function writeAll(items: LocalNotification[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
}

export function pushLocalNotification(
  input: Omit<LocalNotification, "id" | "createdAt" | "read">,
): LocalNotification {
  const item: LocalNotification = {
    ...input,
    id: `ln-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    createdAt: new Date().toISOString(),
    read: false,
  };
  const items = readAll();
  items.unshift(item);
  writeAll(items.slice(0, 100));
  window.dispatchEvent(new CustomEvent("hospitol:notifications"));
  return item;
}

export function listLocalNotificationsForUser(input: {
  userId: string;
  roleSlugs: string[];
}): LocalNotification[] {
  return readAll().filter((item) => {
    if (item.targetUserId && item.targetUserId === input.userId) return true;
    if (item.targetRole && input.roleSlugs.includes(item.targetRole)) return true;
    return !item.targetUserId && !item.targetRole;
  });
}

export function countUnreadLocalNotifications(input: {
  userId: string;
  roleSlugs: string[];
}): number {
  return listLocalNotificationsForUser(input).filter((item) => !item.read).length;
}

export function markLocalNotificationRead(id: string): void {
  const items = readAll().map((item) => (item.id === id ? { ...item, read: true } : item));
  writeAll(items);
  window.dispatchEvent(new CustomEvent("hospitol:notifications"));
}

export function markAllLocalNotificationsRead(input: {
  userId: string;
  roleSlugs: string[];
}): void {
  const visible = new Set(
    listLocalNotificationsForUser(input).map((item) => item.id),
  );
  const items = readAll().map((item) =>
    visible.has(item.id) ? { ...item, read: true } : item,
  );
  writeAll(items);
  window.dispatchEvent(new CustomEvent("hospitol:notifications"));
}
